// Single source of truth for the HubSpot-stage-driven client journey.
// Stage IDs are NOT numerically sequential — order is defined explicitly here.

export const STAGE_ORDER = [
  '3779796680', '3779796681',                        // Onboarding
  // Search (SRL). Retired search ids first so they define the "search reached" threshold.
  '3779796682', '3779796683', '3779796684',          // (retired) Search Completed / Approved
  '4321107658', '4321107659', '4321107660',          // B2, B3, B4
  '4321107661', '4321107662',                        // B5, B6
  // Prep / draft application
  '4322435776', '4322435777', '4322435778',          // C1, C2, C3
  '3779796685', '3779796686', '3779796687',          // Specimen / App Drafted / (retired) Ready to file
  // Filed
  '3779182266',                                      // C4. App Filed, Not Assigned  (filed threshold)
  '3779182267',                                      // C5. Assigned to Examining Attorney (review threshold)
  // USPTO Review / Office Action
  '4322435780', '4321107663', '4321107664',          // D1, D2, D3
  '3779796688', '3779796689', '3779796690',          // (retired) OA Received / Needs Attention / D4 Responded
  '4321594099', '4295976680',                        // D5 OA Maintained / D11 Suspended
  // Published / Opposition / NOA / SOU
  '3779796691',                                      // (retired) Scheduled to Publish (published threshold)
  '3779796692', '4322435779',                        // (retired) In Opposition Period / C6
  '3779796693', '3779796696',                        // C8 Opposed / C7 Through Opposition
  '4321594100', '4321594101',                        // D6 / D7 Notice of Allowance
  '4321594102', '4321594103', '3779796694', '3779796695', // D8 / D9 SOU / (retired) SOU Needed / D10 Submitted
  // Registered
  '3779796697', '3779796698',                        // C9 Registration Confirmed / Closed - Registered
];
export const ORD = Object.fromEntries(STAGE_ORDER.map((id, i) => [id, i]));

export const CLOSED_STAGE = '3779796699';
export const PAUSED_STAGE = '3779181305';
export const REGISTERED_STAGES = ['3779796697', '3779796698'];

const PHASE_TO_STAGE = {
  filed: '3779182266',
  office_action: '3779796688',
  office_action_cleared: '3779796690',
  published: '3779796691',
  opposition_cleared: '3779796696',
  registered: '3779796697',
};

// Resolve a matter to a canonical HubSpot stage id, falling back to status/journey
// for manually-created matters that never came through HubSpot.
export function resolveStageId(m) {
  if (!m) return '3779796680';
  const hs = m.hubspot_stage_id;
  if (hs && (ORD[hs] !== undefined || hs === PAUSED_STAGE || hs === CLOSED_STAGE)) return hs;
  if (['dead', 'abandoned'].includes(m.status_phase)) return CLOSED_STAGE;
  if (m.status_phase === 'onboarding') {
    if (m.journey_phase === 'SEARCH_READY') return '3779796682';
    if (m.journey_phase === 'PREP_FILING') return '3779796685';
    return '3779796680';
  }
  return PHASE_TO_STAGE[m.status_phase] || '3779796680';
}

export const stageOrdinal = (id) => (ORD[id] !== undefined ? ORD[id] : -1);
export const isClosed = (m) => resolveStageId(m) === CLOSED_STAGE;
export const isPaused = (m) => resolveStageId(m) === PAUSED_STAGE;

export const NODES = [
  { key: 'onboarded', label: 'Onboarded' },
  { key: 'search', label: 'Search' },
  { key: 'filed', label: 'Filed' },
  { key: 'review', label: 'USPTO Review' },
  { key: 'published', label: 'Published' },
  { key: 'registered', label: 'Registered' },
];

export function nodeReached(nodeKey, m) {
  const id = resolveStageId(m);
  const o = stageOrdinal(id);
  switch (nodeKey) {
    case 'onboarded': return true;
    case 'search': return o >= ORD['3779796682'];
    case 'filed': return !!m.serial_number || o >= ORD['3779182266'] || id === CLOSED_STAGE;
    case 'review': return o >= ORD['3779182267'];
    case 'published': return o >= ORD['3779796691'];
    case 'registered': return REGISTERED_STAGES.includes(id);
    default: return false;
  }
}

export function currentNodeIndex(m) {
  if (isPaused(m)) {
    if (m.registration_number) return 5;
    if (m.serial_number) return 2;
    return 0;
  }
  let idx = 0;
  NODES.forEach((n, i) => { if (nodeReached(n.key, m)) idx = i; });
  return idx;
}

// Captions render UNDER the relevant node — Office Action / Opposition / SOU etc.
const CAPTIONS = {
  '3779796684': { node: 'search', text: 'Search approved' },
  '3779796685': { node: 'search', text: 'Preparing your application' },
  '3779796686': { node: 'search', text: 'Application drafted' },
  '3779796687': { node: 'search', text: 'Ready to file' },
  '3779182266': { node: 'filed', text: 'Awaiting examiner assignment' },
  '3779796688': { node: 'review', text: 'Office Action received' },
  '3779796689': { node: 'review', text: 'Office Action — needs you' },
  '3779796690': { node: 'review', text: 'Response filed' },
  '3779796692': { node: 'published', text: 'Opposition period open' },
  '3779796693': { node: 'published', text: 'Opposition filed' },
  '3779796696': { node: 'published', text: 'Opposition cleared' },
  '3779796694': { node: 'published', text: 'Statement of Use needed' },
  '3779796695': { node: 'published', text: 'Statement of Use filed' },
  // 2026 pipeline
  '4321107660': { node: 'search', text: 'Report on its way' },              // B4
  '4321107661': { node: 'search', text: 'Search complete — review your report' }, // B5
  '4321107662': { node: 'search', text: 'Search complete — review your report' }, // B6
  '4322435776': { node: 'search', text: 'Preparing your application' },     // C1
  '4322435777': { node: 'search', text: 'Preparing your application' },     // C2
  '4322435778': { node: 'search', text: 'Ready to file' },                  // C3
  '4322435779': { node: 'published', text: 'Opposition period open' },      // C6
  '4322435780': { node: 'review', text: 'Office Action received' },         // D1
  '4321107663': { node: 'review', text: 'Office Action — needs you' },      // D2
  '4321107664': { node: 'review', text: 'Responding to the Office Action' },// D3
  '4321594099': { node: 'review', text: 'Reviewing options after the Office Action' }, // D5
  '4295976680': { node: 'review', text: 'Suspended by the USPTO' },         // D11
  '4321594100': { node: 'published', text: 'Notice of Allowance received' },// D6
  '4321594101': { node: 'published', text: 'Notice of Allowance — needs you' }, // D7
  '4321594102': { node: 'published', text: 'Preparing your Statement of Use' }, // D8
  '4321594103': { node: 'published', text: 'Statement of Use — review needed' }, // D9
};

// B05/B06: the search is complete and the client is meant to review their report. When the
// report link is present the timeline caption becomes a clickable link to open it.
export const SEARCH_READY_REVIEW_STAGES = ['4321107661', '4321107662'];

export function captionFor(m) {
  if (isClosed(m)) return null;
  if (isPaused(m)) return { nodeIndex: currentNodeIndex(m), text: 'On hold' };
  const id = resolveStageId(m);
  const c = CAPTIONS[id];
  if (!c) return null;
  // B05/B06: link the caption to the report when it exists; otherwise show a
  // non-misleading "on its way" caption that matches the card's awaiting panel.
  if (SEARCH_READY_REVIEW_STAGES.includes(id)) {
    const hasUrl = !!m.search_report_url;
    return {
      nodeIndex: NODES.findIndex((n) => n.key === c.node),
      text: hasUrl ? 'Search complete — review your report' : 'Search complete — report on its way',
      link: hasUrl,
    };
  }
  return { nodeIndex: NODES.findIndex((n) => n.key === c.node), text: c.text, link: false };
}

// Colors (status/tier palette allowed on chips — NOT on buttons/links)
const NAVY = '#141b4d', COBALT = '#1d57a5', YELLOW = '#ffd100', AMBER = '#c2410c', EVERGREEN = '#006747', GREY = '#9a9488';
const CHIPS = {
  '3779796680': { label: 'Onboarding', color: COBALT },
  '3779796681': { label: 'Onboarding', color: COBALT },
  '3779796682': { label: 'Search complete', color: YELLOW },
  '3779796683': { label: 'Search complete', color: YELLOW },
  '3779796684': { label: 'Preparing your application', color: COBALT },
  '3779796685': { label: 'Preparing your application', color: COBALT },
  '3779796686': { label: 'Preparing your application', color: COBALT },
  '3779796687': { label: 'Preparing your application', color: COBALT },
  '3779182266': { label: 'Filed with the USPTO', color: NAVY },
  '3779182267': { label: 'Filed with the USPTO', color: NAVY },
  '3779796688': { label: "We're reviewing it", color: NAVY },
  '3779796689': { label: 'Needs you', color: AMBER },
  '3779796690': { label: 'Response filed', color: EVERGREEN },
  '3779796691': { label: 'Published for opposition', color: NAVY },
  '3779796692': { label: 'Published for opposition', color: NAVY },
  '3779796693': { label: 'Opposition filed', color: AMBER },
  '3779796696': { label: 'Published for opposition', color: NAVY },
  '3779796694': { label: 'Statement of Use needed', color: AMBER },
  '3779796695': { label: 'Published for opposition', color: NAVY },
  '3779796697': { label: 'Registered', color: EVERGREEN },
  '3779796698': { label: 'Registered', color: EVERGREEN },
  [CLOSED_STAGE]: { label: 'Closed', color: GREY },
  [PAUSED_STAGE]: { label: 'On hold', color: GREY },
  // 2026 pipeline
  '4321107658': { label: 'Search underway', color: COBALT },      // B2
  '4321107659': { label: 'Search underway', color: COBALT },      // B3
  '4321107660': { label: 'Search underway', color: COBALT },      // B4
  '4321107661': { label: 'Search complete', color: YELLOW },      // B5
  '4321107662': { label: 'Search complete', color: YELLOW },      // B6
  '4322435776': { label: 'Preparing your application', color: COBALT }, // C1
  '4322435777': { label: 'Preparing your application', color: COBALT }, // C2
  '4322435778': { label: 'Preparing your application', color: COBALT }, // C3
  '4322435779': { label: 'Published for opposition', color: NAVY },// C6
  '4322435780': { label: "We're reviewing it", color: NAVY },     // D1
  '4321107663': { label: 'Needs you', color: AMBER },             // D2
  '4321107664': { label: 'Response in progress', color: NAVY },   // D3
  '4321594099': { label: 'Under USPTO review', color: NAVY },     // D5
  '4295976680': { label: 'Suspended by USPTO', color: GREY },     // D11
  '4321594100': { label: 'Notice of Allowance', color: NAVY },    // D6
  '4321594101': { label: 'Needs you', color: AMBER },             // D7
  '4321594102': { label: 'Statement of Use', color: NAVY },       // D8
  '4321594103': { label: 'Statement of Use needed', color: AMBER },// D9
};
export function chipFor(m) {
  return CHIPS[resolveStageId(m)] || { label: 'In progress', color: NAVY };
}
// Chip text color: navy on yellow, white on everything else.
export const chipTextColor = (bg) => (bg === YELLOW ? NAVY : '#ffffff');

// "Needs your attention": OA-needs-you, opposition filed, SOU needed, or an
// un-decided search report (client hasn't chosen Proceed/Questions/Pause yet).
export function needsAttention(m) {
  const id = resolveStageId(m);
  if (['3779796689', '3779796693', '3779796694',
       '4321107663', '4321594101', '4321594103'].includes(id)) return true;
  if (['3779796682', '3779796683', '4321107661', '4321107662'].includes(id)
      && m.search_report_url && !m.client_decision) return true;
  return false;
}

// Stages after the search is approved but before the application is filed.
export const PREPARING_STAGES = ['3779796684', '3779796685', '3779796686', '3779796687',
  '4322435776', '4322435777', '4322435778'];
export const isPreparing = (m) => PREPARING_STAGES.includes(resolveStageId(m));

// Quiet publication / opposition-window stages (nothing needed from the client).
export const PUBLICATION_STAGES = ['3779796691', '3779796692', '3779796696', '4322435779'];
export const isPublication = (m) => PUBLICATION_STAGES.includes(resolveStageId(m));

export const isRegistered = (m) => REGISTERED_STAGES.includes(resolveStageId(m)) || m.status_phase === 'registered';

// Grammatical status for the single-matter header sentence: "Your trademark X {verb} {noun}."
export function phaseSummary(m) {
  if (isClosed(m)) return { verb: 'is', noun: 'closed' };
  if (isPaused(m)) return { verb: 'is', noun: 'on hold' };
  if (isRegistered(m)) return { verb: 'is', noun: 'registered' };
  const key = NODES[currentNodeIndex(m)]?.key;
  const noun = {
    onboarded: 'onboarding',
    search: 'the trademark search stage',
    filed: 'USPTO review',
    review: 'USPTO review',
    published: 'the publication stage',
    registered: 'registered',
  }[key] || 'progress';
  return { verb: 'is in', noun };
}
